import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live social proof strip.
 * - Pulls actual Brevo list subscribers via /api/signup/attendees.
 * - Auto-refreshes on mount, on `refreshKey` changes, and every 45s.
 */
export default function AttendeeStrip({ refreshKey = 0, compact = false }) {
    const [data, setData] = useState({ count: 0, initials: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        let timer;
        const fetchData = async () => {
            try {
                const r = await axios.get(`${API}/signup/attendees?limit=10`);
                if (mounted) {
                    setData({
                        count: r.data?.count ?? 0,
                        initials: r.data?.initials ?? [],
                    });
                }
            } catch (_) {
                /* non-blocking */
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchData();
        timer = setInterval(fetchData, 45000);
        return () => {
            mounted = false;
            if (timer) clearInterval(timer);
        };
    }, [refreshKey]);

    const shown = data.initials.slice(0, compact ? 5 : 7);
    const extra = Math.max(0, data.count - shown.length);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            data-testid="attendee-strip"
            className="flex items-center gap-4 bg-white/75 backdrop-blur-md border border-slate-200 rounded-full pl-2 pr-5 py-2 shadow-sm w-full"
        >
            <div className="flex -space-x-2.5" data-testid="avatar-stack">
                {shown.length === 0 && !loading ? (
                    <div className="w-9 h-9 rounded-full border-[3px] border-white bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-400">
                        1st
                    </div>
                ) : (
                    shown.map((a, idx) => (
                        <div
                            key={`${a.initials}-${idx}`}
                            className={`w-9 h-9 rounded-full border-[3px] border-white flex items-center justify-center text-[11px] font-bold shadow-sm ${a.color}`}
                            title={a.initials}
                        >
                            {a.initials}
                        </div>
                    ))
                )}
                {extra > 0 && (
                    <div className="h-9 px-3 rounded-full border-[3px] border-white bg-slate-900 text-white flex items-center justify-center text-[9px] font-black uppercase tracking-tighter whitespace-nowrap">
                        & more
                    </div>
                )}
            </div>
            <div className="text-left leading-tight">
                <p className="text-sm font-semibold text-slate-900">
                    <span
                        data-testid="attendee-count"
                        className="text-[#0D9488] font-bold text-xl"
                    >
                        {data.count}
                    </span>
                </p>
            </div>
        </motion.div>
    );
}
